import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Award, TrendingDown, Scale, Plus, Pencil, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ConferenceLog, { hasConference } from "@/components/ConferenceLog";
import PageHeader, { ActionRow, EmptyState, fmtDate, fmtTime } from "@/components/PageHeader";
import { useFeatureFlags } from "@/lib/featureFlags";
import { shortNo } from "@/lib/caseNo";

function AckDialog({ caseItem, onDone, autoOpen, fullWidth }) {
  const { schedule: scheduleOn } = useFeatureFlags();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState("no_further_action_needed");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/member/merits/${caseItem.id}/acknowledge`, { choice });
      toast.success("Acknowledgement submitted");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="lg"
          className={`rounded-full bg-amber-500 font-bold text-white shadow-md ring-1 ring-amber-600/20 transition-colors hover:bg-amber-600 ${fullWidth ? "h-12 w-full text-base" : ""}`}
          data-testid={`acknowledge-btn-${caseItem.case_number}`}
        >
          <CheckCircle2 className={`mr-2 ${fullWidth ? "h-5 w-5" : "h-4 w-4"}`} /> Acknowledge
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="acknowledge-dialog">
        <DialogHeader><DialogTitle className="font-heading">Acknowledge case {shortNo(caseItem.case_number)}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2" data-testid="acknowledge-type-value">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide ${caseItem.value > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{caseItem.case_type}</span>
          <span className={`text-lg font-extrabold tabular-nums ${caseItem.value > 0 ? "text-emerald-600" : "text-red-600"}`}>{caseItem.value > 0 ? `+${caseItem.value}` : caseItem.value}</span>
        </div>
        <p className="text-sm text-slate-500">{caseItem.description}</p>
        <RadioGroup value={choice} onValueChange={setChoice} className="mt-2 space-y-2">
          <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
            <RadioGroupItem value="no_further_action_needed" id="opt1" data-testid="ack-option-no-action" />
            <Label htmlFor="opt1" className="cursor-pointer">No further action needed</Label>
          </div>
          {scheduleOn && (
          <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
            <RadioGroupItem value="conference_requested" id="opt2" data-testid="ack-option-conference" />
            <Label htmlFor="opt2" className="cursor-pointer">Request a conference</Label>
          </div>
          )}
        </RadioGroup>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="ack-submit-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestDialog({ open, setOpen, editing, onDone }) {
  const [presets, setPresets] = useState([]);
  const [form, setForm] = useState({ preset_id: "", event_date: "", event_description: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) api.get("/member/merit-presets").then(({ data }) => setPresets(data.items || [])).catch(() => setPresets([]));
  }, [open]);
  useEffect(() => {
    if (editing) setForm({ preset_id: editing.preset_id || "", event_date: (editing.event_date || "").slice(0, 10), event_description: editing.event_description || "" });
    else setForm({ preset_id: "", event_date: "", event_description: "" });
  }, [editing, open]);

  const selected = presets.find((p) => p.id === form.preset_id);
  const submit = async () => {
    if (!form.preset_id || !form.event_date || !form.event_description.trim()) {
      toast.error("Merit event, date and description are all required"); return;
    }
    setLoading(true);
    try {
      const payload = { preset_id: form.preset_id, event_date: form.event_date, event_description: form.event_description };
      if (editing) { await api.put(`/member/merit-requests/${editing.id}`, payload); toast.success("Request updated"); }
      else { await api.post("/member/merit-requests", payload); toast.success("Merit request submitted"); }
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="merit-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? `Edit request ${shortNo(editing.request_number)}` : "Request merit points"}</DialogTitle>
          <DialogDescription>Pick a merit event, add the date and describe what happened. Your coach will review it.</DialogDescription>
        </DialogHeader>
        {presets.length === 0 ? (
          <p className="py-4 text-sm text-slate-500" data-testid="no-merit-presets">
            There are no merit events available for your team yet. Please ask your coach to set up merit events before requesting points.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Merit event</Label>
              <Select value={form.preset_id} onValueChange={(v) => setForm((f) => ({ ...f, preset_id: v }))}>
                <SelectTrigger data-testid="request-event-select" className="h-auto min-h-9 items-start whitespace-normal py-2 text-left [&>span]:line-clamp-none [&>span]:whitespace-normal [&>span]:break-words"><SelectValue placeholder="Select a merit event" /></SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  {presets.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`request-preset-${p.id}`}
                      className="items-start whitespace-normal break-words pr-8 leading-snug">
                      {p.name} · {p.value_type === "range" ? `${p.min}–${p.max} pts` : `${p.value} pts`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected && (
                <p className="text-xs font-medium text-slate-500" data-testid="request-value-hint">
                  {selected.value_type === "single"
                    ? `This event awards ${selected.value} merit point${selected.value === 1 ? "" : "s"}.`
                    : `Your coach will assign between ${selected.min} and ${selected.max} points when approving.`}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Date of event</Label>
              <Input type="date" value={form.event_date} onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))} data-testid="request-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea rows={3} value={form.event_description} onChange={(e) => setForm((f) => ({ ...f, event_description: e.target.value }))} placeholder="Briefly describe the event" data-testid="request-description-input" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={loading || presets.length === 0} data-testid="request-submit-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">{editing ? "Save" : "Submit request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MemberMerits() {
  const { schedule: scheduleOn } = useFeatureFlags();
  const [data, setData] = useState({ items: [], total_merits: 0, total_demerits: 0, net_score: 0 });
  const [requests, setRequests] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [editingReq, setEditingReq] = useState(null);

  // Demerits never require acknowledgement when the Daily Schedule feature is off.
  const canAck = (m) => !m.acknowledgement_submitted && (scheduleOn || m.value > 0);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/member/merits"), api.get("/member/merit-requests")]);
    setData(a.data); setRequests(b.data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;
  // Sort by status priority: Awaiting Acknowledgement → Conference Scheduled → Repealed → No Further Action
  const rank = (m) => {
    if (!m.acknowledgement_submitted || m.status === "no_action_yet") return 0;
    if (m.status === "conference_requested" || m.status === "conference_date") return 1;
    if (m.status === "rescinded") return 2;
    if (m.status === "no_further_action_needed") return 3;
    return 4;
  };
  const sorted = [...data.items].sort((a, b) => {
    const r = rank(a) - rank(b);
    return r !== 0 ? r : (b.date_issued || "").localeCompare(a.date_issued || "");
  });
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const [params] = useSearchParams();
  const ackFlag = !!params.get("ack");
  const firstAwaitId = sorted.find((m) => !m.acknowledgement_submitted)?.id;
  const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;

  return (
    <div>
      <PageHeader title="Merit System" subtitle="Merit and demerit cases issued to you" />
      {/* Compact horizontal summary bar */}
      <Card className="mb-6 flex items-stretch divide-x divide-slate-200 border-slate-200 shadow-sm" data-testid="merits-summary-bar">
        <div className="flex flex-1 items-center gap-2.5 px-3 py-3 sm:px-5" data-testid="merits-total-merits">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600"><Award className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs">Merits</p>
            <p className="text-lg font-extrabold tabular-nums text-emerald-600 sm:text-xl">{data.total_merits}</p>
          </div>
        </div>
        <div className="flex flex-1 items-center gap-2.5 px-3 py-3 sm:px-5" data-testid="merits-total-demerits">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600"><TrendingDown className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs">Demerits</p>
            <p className="text-lg font-extrabold tabular-nums text-red-600 sm:text-xl">{data.total_demerits}</p>
          </div>
        </div>
        <div className="flex flex-1 items-center gap-2.5 px-3 py-3 sm:px-5" data-testid="merits-net-score">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600"><Scale className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs">Net Score</p>
            <p className="text-lg font-extrabold tabular-nums text-sky-600 sm:text-xl">{data.net_score}</p>
          </div>
        </div>
      </Card>

      {/* Request merit, then my requests, then the official record */}
      <ActionRow testId="request-merit-row">
        <Button onClick={() => { setEditingReq(null); setReqOpen(true); }} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="request-merit-btn"><Plus className="mr-2 h-4 w-4" /> Request merit</Button>
      </ActionRow>
      <div data-testid="my-merit-requests-section">
        <h3 className="mb-3 text-lg font-semibold text-slate-800 font-heading">My Merit Requests</h3>
        {requests.length === 0 ? (
          <EmptyState title="No merit requests yet" description="Submit a request for merit points and your coach will review it." testId="requests-empty" />
        ) : (
          <Card className="border-slate-200 shadow-sm overflow-x-auto">
            <Table data-testid="merit-requests-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Request #", "Event", "Event Date", "Description", "Status", "Awarded", ""].map((h) => (
                    <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} data-testid={`request-row-${r.request_number}`}>
                    <TableCell className="font-mono text-xs font-semibold">{shortNo(r.request_number)}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-800">{r.event_name}</TableCell>
                    <TableCell className="text-sm">{fmtDate(r.event_date)}</TableCell>
                    <TableCell className="max-w-xs text-sm text-slate-600">
                      {r.event_description}
                      {r.status === "returned_for_correction" && r.returned_comment && (
                        <p className="mt-1 text-xs font-semibold text-violet-600" data-testid={`request-returned-comment-${r.request_number}`}>Coach: {r.returned_comment}</p>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="font-bold tabular-nums text-emerald-600" data-testid={`request-awarded-${r.request_number}`}>
                      {r.status === "approved" && r.assigned_value ? `+${r.assigned_value}`
                        : r.status === "disapproved" ? "—"
                        : r.value_type === "single" && r.assigned_value ? `${r.assigned_value} pts`
                        : r.value_type === "range" ? `${r.preset_min ?? 1}–${r.preset_max ?? 10} pts`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {(r.status === "pending" || r.status === "returned_for_correction") && (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEditingReq(r); setReqOpen(true); }} data-testid={`edit-request-${r.request_number}`}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <div className="mt-8" data-testid="cases-issued-section">
      <h3 className="mb-3 text-lg font-semibold text-slate-800 font-heading">Cases Issued to Me</h3>
      {data.items.length === 0 ? (
        <EmptyState title="No merit cases" description="Cases issued by your coach will appear here." testId="merits-empty" />
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden md:block border-slate-200 shadow-sm overflow-hidden">
            <Table data-testid="merits-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Case #", "Date", "Type", "Value", "Description", "Acknowledged", "Status", ""].map((h) => (
                    <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((m) => (
                  <TableRow key={m.id} data-testid={`merit-row-${m.case_number}`}>
                    <TableCell className="font-mono text-xs font-semibold">{shortNo(m.case_number)}</TableCell>
                    <TableCell className="text-sm">{fmtDate(m.date_issued)}</TableCell>
                    <TableCell><StatusBadge status={m.case_type} /></TableCell>
                    <TableCell className={`font-bold tabular-nums ${m.value > 0 ? "text-emerald-600" : "text-red-600"}`}>{m.value > 0 ? `+${m.value}` : m.value}</TableCell>
                    <TableCell className="max-w-xs text-sm text-slate-600">{m.description}</TableCell>
                    <TableCell className="text-sm">{fmtDate(m.acknowledgement_date)}</TableCell>
                    <TableCell>
                      <StatusBadge status={m.status} />
                      {m.conference_date && (
                        <p className="mt-1 text-xs font-semibold text-blue-600" data-testid={`conference-scheduled-${m.case_number}`}>
                          Conference Scheduled for {fmtDate(m.conference_date)}{m.conference_time ? ` at ${fmtTime(m.conference_time)}` : ""}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {canAck(m) && <AckDialog caseItem={m} onDone={load} autoOpen={ackFlag && isDesktop && m.id === firstAwaitId} />}
                        {hasConference(m) && <ConferenceLog caseItem={m} />}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden" data-testid="merits-cards">
            {paged.map((m) => (
              <Card key={m.id} className="p-4 border-slate-200" data-testid={`merit-card-${m.case_number}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold text-slate-700">{shortNo(m.case_number)}</span>
                  <span className={`text-lg font-bold tabular-nums ${m.value > 0 ? "text-emerald-600" : "text-red-600"}`}>{m.value > 0 ? `+${m.value}` : m.value}</span>
                </div>
                <p className="mt-2 text-sm text-slate-600">{m.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StatusBadge status={m.case_type} />
                  <StatusBadge status={m.status} />
                  <span className="text-xs text-slate-400">{fmtDate(m.date_issued)}</span>
                </div>
                {m.conference_date && (
                  <p className="mt-2 text-xs font-semibold text-blue-600" data-testid={`conference-scheduled-card-${m.case_number}`}>
                    Conference Scheduled for {fmtDate(m.conference_date)}{m.conference_time ? ` at ${fmtTime(m.conference_time)}` : ""}
                  </p>
                )}
                {(canAck(m) || hasConference(m)) && (
                  <div className="mt-4 space-y-2">
                    {canAck(m) && <AckDialog caseItem={m} onDone={load} fullWidth />}
                    {hasConference(m) && <div className="flex justify-center"><ConferenceLog caseItem={m} /></div>}
                  </div>
                )}
              </Card>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-slate-500" data-testid="cases-page-label">Page {current} of {totalPages} · {sorted.length} cases</span>
            {totalPages > 1 && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-full" disabled={current <= 1} onClick={() => setPage(current - 1)} data-testid="cases-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" className="rounded-full" disabled={current >= totalPages} onClick={() => setPage(current + 1)} data-testid="cases-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        </>
      )}
      </div>

      <RequestDialog open={reqOpen} setOpen={setReqOpen} editing={editingReq} onDone={load} />
    </div>
  );
}
